import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Loader2,
  PauseCircle,
  PlayCircle,
  RefreshCcw,
  Shield,
  StopCircle,
  UserCircle2,
  UserPlus,
  X,
} from 'lucide-react';
import { useExecutionDetails } from '../hooks/useExecutionDetails';
import { useExecutionMutations } from '../hooks/useExecutionMutations';
import { useExecutionRealtime } from '../hooks/useExecutionRealtime';
import { useInstanceAudit } from '../hooks/useInstanceAudit';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { PermissionKey } from '../../../types/workspace';
import {
  getExecutionRemainingSlaMinutes,
  getExecutionTotalSlaMinutes,
  getOperationalRiskLevel,
  formatSlaSmartLabel,
} from '../../shared/utils/operationalRisk';
import type { ExecutionTimelineEntry } from '../../../services/executionService';
import { useAppToast } from '../../../components/AppToast';

interface ExecutionDrawerHostProps {
  entityId: string;
  organizationId: string;
  mode?: string | null;
  onClose: () => void;
}

export function ExecutionDrawerHost({ entityId, organizationId, mode, onClose }: ExecutionDrawerHostProps) {
  const { workspace } = useWorkspace();
  const canCancel = workspace?.permissions[PermissionKey.CAN_CANCEL_EXECUTION] ?? false;
  const canPause = workspace?.permissions[PermissionKey.CAN_PAUSE_EXECUTION] ?? false;
  const canReprocess = workspace?.permissions[PermissionKey.CAN_REPROCESS_EXECUTION] ?? false;
  const { showToast, ToastContainer } = useAppToast();

  const detailsQuery = useExecutionDetails(organizationId, entityId);
  const { audit } = useInstanceAudit(organizationId, entityId);
  const mutations = useExecutionMutations({ organizationId });
  useExecutionRealtime(entityId, organizationId, Boolean(entityId));

  const [isReprocessOpen, setIsReprocessOpen] = useState(false);
  const [reprocessReason, setReprocessReason] = useState('');
  const [drawerTab, setDrawerTab] = useState<'operacional' | 'tecnico'>('operacional');

  const execution = detailsQuery.data?.instance;
  const timeline = useMemo(() => detailsQuery.data?.timeline ?? [], [detailsQuery.data?.timeline]);
  const timelinePreview = timeline.slice(0, 6);
  const currentTask = useMemo(
    () =>
      (detailsQuery.data?.tasks ?? []).find((task) => task.status === 'in_progress') ??
      (detailsQuery.data?.tasks ?? []).find((task) => task.status === 'pending') ??
      (detailsQuery.data?.tasks ?? [])[0] ??
      null,
    [detailsQuery.data?.tasks],
  );
  const executorLabel =
    currentTask?.assignedUserName ??
    currentTask?.assignedToName ??
    currentTask?.assignedRoleName ??
    'Não atribuído';
  const roleLabel = currentTask?.assignedRoleName ?? currentTask?.assignedRoleKey ?? 'Não definido';

  const canTriggerReprocess = useMemo(() => {
    if (!execution || !canReprocess) return false;
    return execution.status === 'failed' || execution.status === 'cancelled' || execution.status === 'completed';
  }, [execution, canReprocess]);

  const actionBusy =
    mutations.pauseExecution.isLoading ||
    mutations.resumeExecution.isLoading ||
    mutations.cancelExecution.isLoading ||
    mutations.reprocessExecution.isLoading;

  const handleAction = async (action: 'pause' | 'resume' | 'cancel') => {
    if (!execution) return;
    try {
      if (action === 'pause') {
        await mutations.pauseExecution.mutate(execution.id);
                showToast('info', 'Execução pausada com sucesso.');
      }
      if (action === 'resume') {
        await mutations.resumeExecution.mutate(execution.id);
        showToast('success', 'Execução retomada.');
      }
      if (action === 'cancel') {
        await mutations.cancelExecution.mutate({ executionId: execution.id });
        showToast('warning', 'Execução cancelada.')
      }
      await detailsQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nao foi possivel executar esta acao.';
      showToast('error', message);
    }
  };

  const handleReprocess = async () => {
    if (!execution || !canTriggerReprocess) return;
    try {
      const result = await mutations.reprocessExecution.mutate({
        executionId: execution.id,
        reason: reprocessReason.trim() || undefined,
      });
      showToast('success', 'Nova execução criada a partir deste histórico.');
      setIsReprocessOpen(false);
      setReprocessReason('');
      await detailsQuery.refetch();
      if (result?.id) {
        window.setTimeout(() => {
          showToast('info', `Acompanhando a nova instância ${result.referenceCode ?? result.id}.`);
        }, 300);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao reprocessar execução.';
      showToast('error', message);
    }
  };

  const scrollToSection = (targetId: string) => {
    const element = document.querySelector(`[data-execution-section="${targetId}"]`);
    if (element && 'scrollIntoView' in element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (mode === 'timeline') {
      scrollToSection('timeline');
      return;
    }
    if (mode === 'actions') {
      scrollToSection('actions');
      return;
    }
    if (mode === 'reprocess') {
      setIsReprocessOpen(true);
      window.setTimeout(() => scrollToSection('actions'), 30);
    }
  }, [mode]);

  const renderContent = () => {
    if (detailsQuery.isLoading || !execution) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 size={22} className="animate-spin text-slate-500" />
        </div>
      );
    }

    const remainingMinutes = getExecutionRemainingSlaMinutes(execution);
    const totalSla = getExecutionTotalSlaMinutes(execution);
    const riskLevel = getOperationalRiskLevel(remainingMinutes, totalSla);
    const slaInfo = formatSlaSmartLabel(remainingMinutes);
    const hasSla = slaInfo.status !== 'none';

    const statusMap: Record<string, { label: string; className: string }> = {
      active: { label: 'ATIVO', className: 'bg-blue-50 border-blue-400 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300' },
      on_hold: { label: 'BLOQUEADO', className: 'bg-amber-50 border-amber-400 text-amber-700 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300' },
      completed: { label: 'CONCLUÍDO', className: 'bg-green-50 border-green-400 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300' },
      cancelled: { label: 'CANCELADO', className: 'bg-red-50 border-red-400 text-red-600 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300' },
      failed: { label: 'FALHA', className: 'bg-orange-50 border-orange-400 text-orange-700 dark:bg-orange-900/30 dark:border-orange-600 dark:text-orange-300' },
    };
    const priorityMap: Record<string, { label: string; className: string }> = {
      critica: { label: 'CRÍTICA', className: 'bg-red-50 border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300' },
      alta: { label: 'ALTA', className: 'bg-orange-50 border-orange-400 text-orange-700 dark:bg-orange-900/30 dark:border-orange-600 dark:text-orange-300' },
      media: { label: 'MÉDIA', className: 'bg-yellow-50 border-yellow-400 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-600 dark:text-yellow-300' },
      baixa: { label: 'BAIXA', className: 'bg-slate-50 border-slate-300 text-slate-600 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400' },
    };
    const riskMap: Record<string, { label: string; className: string; icon: typeof AlertTriangle }> = {
      CRITICO: { label: 'CRÍTICO', className: 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900/40 dark:border-red-600 dark:text-red-300', icon: AlertCircle },
      ALERTA: { label: 'ALERTA', className: 'bg-amber-100 border-amber-400 text-amber-700 dark:bg-amber-900/40 dark:border-amber-600 dark:text-amber-300', icon: AlertTriangle },
      OK: { label: 'OK', className: 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300', icon: CheckCircle2 },
    };

    const statusInfo = statusMap[execution.status] ?? { label: execution.status, className: 'border-slate-300 bg-white text-slate-700' };
    const priorityInfo = priorityMap[execution.priority ?? ''] ?? null;
    const riskInfo = riskMap[riskLevel];

    // Last action from timeline
    const lastAction = timeline[0] ?? null;

    // Relative time helper
    const formatRelativeTime = (timestamp: string): string => {
      const diffMs = Date.now() - new Date(timestamp).getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'agora';
      if (diffMin < 60) return `há ${diffMin} min`;
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `há ${diffHours}h`;
      const diffDays = Math.floor(diffHours / 24);
      return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    };

    // Smart "Próxima Ação" logic
    const getNextActionMessage = (): { text: string; severity: 'critical' | 'warning' | 'info' } => {
      if (execution.status === 'completed') return { text: 'Arquivar ou avaliar resultados da execução', severity: 'info' };
      if (execution.status === 'cancelled') return { text: 'Reprocessar ou encerrar definitivamente', severity: 'warning' };
      if (execution.status === 'failed') return { text: 'Investigar causa da falha e reprocessar', severity: 'critical' };
      if (execution.status === 'on_hold') return { text: 'Retomar execução para continuar fluxo', severity: 'warning' };

      const hasAssignee = executorLabel !== 'Não atribuído';
      const taskName = currentTask?.taskName ?? currentTask?.stageName ?? null;

      if (!hasAssignee && taskName) return { text: `Atribuir responsável para "${taskName}"`, severity: 'critical' };
      if (!hasAssignee) return { text: 'Atribuir responsável para iniciar execução', severity: 'critical' };
      if (slaInfo.status === 'violated') return { text: `Escalar ou resolver "${taskName ?? 'tarefa atual'}" — SLA vencido`, severity: 'critical' };
      if (slaInfo.status === 'warning') return { text: `Priorizar "${taskName ?? 'tarefa atual'}" — SLA próximo do limite`, severity: 'warning' };
      if (taskName) return { text: `Executar etapa: ${taskName}`, severity: 'info' };
      return { text: 'Avançar para próxima etapa', severity: 'info' };
    };
    const nextActionMsg = getNextActionMessage();

    // Overall situation (top-level summary)
    const getOverallSituation = (): { label: string; emoji: string; borderClass: string } => {
      if (execution.status === 'failed' || execution.status === 'cancelled')
        return { label: 'Crítica', emoji: '🔴', borderClass: 'border-l-red-500' };
      if (riskLevel === 'CRITICO' || !hasSla)
        return { label: 'Crítica', emoji: '🔴', borderClass: 'border-l-red-500' };
      if (riskLevel === 'ALERTA' || execution.status === 'on_hold')
        return { label: 'Atenção', emoji: '🟡', borderClass: 'border-l-amber-500' };
      if (execution.status === 'completed')
        return { label: 'Concluída', emoji: '🟢', borderClass: 'border-l-green-500' };
      return { label: 'Estável', emoji: '🟢', borderClass: 'border-l-green-500' };
    };
    const overallSituation = getOverallSituation();

    // Idle time (minutes since last movement)
    const idleMinutes = lastAction
      ? Math.floor((Date.now() - new Date(lastAction.timestamp).getTime()) / 60000)
      : execution.startedAt
        ? Math.floor((Date.now() - new Date(execution.startedAt).getTime()) / 60000)
        : null;
    const idleLabel = idleMinutes != null
      ? idleMinutes < 60 ? `${idleMinutes}min` : idleMinutes < 1440 ? `${Math.floor(idleMinutes / 60)}h` : `${Math.floor(idleMinutes / 1440)} dia(s)`
      : null;
    const idleSeverity = idleMinutes != null ? (idleMinutes > 1440 ? 'critical' : idleMinutes > 480 ? 'warning' : 'info') : 'info';

    // Risk level label for block header
    const riskHeaderMap: Record<string, { label: string; emoji: string }> = {
      CRITICO: { label: 'RISCO CRÍTICO', emoji: '🔴' },
      ALERTA: { label: 'RISCO MODERADO', emoji: '🟡' },
      OK: { label: 'CONTROLADO', emoji: '🟢' },
    };
    const riskHeader = !hasSla ? riskHeaderMap.CRITICO : riskHeaderMap[riskLevel];

    // Diagnostics for alert block
    const diagnostics: Array<{ icon: string; text: string; severity: 'critical' | 'warning' | 'info' }> = [];
    if (!hasSla) {
      diagnostics.push({ icon: '🔴', text: 'Etapa sem SLA definido — sem controle de prazo', severity: 'critical' });
    } else if (slaInfo.status === 'violated') {
      diagnostics.push({ icon: '🔴', text: slaInfo.label, severity: 'critical' });
    } else if (slaInfo.status === 'warning') {
      diagnostics.push({ icon: '🟡', text: `SLA próximo: ${slaInfo.label}`, severity: 'warning' });
    } else {
      diagnostics.push({ icon: '🟢', text: `SLA em dia: ${slaInfo.label}`, severity: 'info' });
    }
    if (execution.elapsedMinutes != null && execution.elapsedMinutes > 0) {
      const elapsed = execution.elapsedMinutes;
      const h = Math.floor(elapsed / 60);
      const m = Math.round(elapsed % 60);
      const elapsedText = h > 24 ? `${h}h em aberto → atraso extremo` : `${h > 0 ? `${h}h ` : ''}${m}min em aberto`;
      diagnostics.push({ icon: '⏱', text: elapsedText, severity: elapsed > 1440 ? 'critical' : elapsed > 480 ? 'warning' : 'info' });
    }
    if ((execution.reprocessCount ?? 0) > 0) {
      diagnostics.push({ icon: '🔁', text: `${execution.reprocessCount} reprocesso(s)`, severity: 'warning' });
    } else {
      diagnostics.push({ icon: '🔁', text: '0 reprocessos', severity: 'info' });
    }
    if (execution.status === 'on_hold' && execution.metadata?.onHoldSince) {
      diagnostics.push({ icon: '⏸', text: `Parado ${formatRelativeTime(execution.metadata.onHoldSince as string)}`, severity: 'warning' });
    }

    if (drawerTab === 'tecnico') {
      return (
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Dados Técnicos</p>
          <div className="grid grid-cols-2 gap-px overflow-hidden border border-slate-300/80 bg-slate-300/80 dark:border-slate-800 dark:bg-slate-800">
            {[
              { label: 'ID do Modelo', value: audit?.blueprintId },
              { label: 'Versão', value: audit?.versionSemver },
              { label: 'Snapshot', value: audit?.snapshotHash },
              { label: 'Template ID', value: audit?.templateId ?? execution.templateId },
              { label: 'Instance ID', value: execution.id },
              { label: 'Reprocessos', value: String(execution.reprocessCount ?? 0) },
            ].map((cell) => (
              <div key={cell.label} className="bg-white px-4 py-2.5 dark:bg-[var(--bg-surface)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{cell.label}</p>
                <p className="mt-1 truncate font-mono text-[11px] text-slate-900 dark:text-slate-100">{cell.value ?? 'N/D'}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 pt-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Histórico Completo</p>
            {timeline.length === 0 ? <p className="text-xs text-slate-500">Nenhum evento registrado.</p> : null}
            {timeline.map((entry) => (
              <TimelineItem key={entry.id} entry={entry} />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 space-y-0 overflow-y-auto">

        {/* ── BLOCO 0: SITUAÇÃO GERAL ── */}
        <section className={`border-b border-l-4 px-5 py-2.5 ${overallSituation.borderClass} border-b-slate-200 dark:border-b-slate-800`}>
          <div className="flex items-center gap-2">
            <span className="text-[16px] leading-none">{overallSituation.emoji}</span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Situação Geral</p>
              <p className="font-mono text-[14px] font-bold uppercase tracking-[0.08em] text-slate-900 dark:text-slate-50">
                {overallSituation.label}
              </p>
            </div>
          </div>
        </section>

        {/* ── BLOCO 1: STATUS ── */}
        <section className="border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <p className="font-mono text-[22px] font-bold tracking-tight text-slate-900 dark:text-slate-50">
            {execution.referenceCode ?? 'Sem código'}
          </p>
          {execution.title && execution.title !== execution.referenceCode && (
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{execution.title}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] ${statusInfo.className}`}>
              {statusInfo.label}
            </span>
            {priorityInfo && (
              <span className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em] ${priorityInfo.className}`}>
                {priorityInfo.label}
              </span>
            )}
          </div>
          {execution.currentStageName && (
            <div className="mt-2.5 flex items-center gap-2 border-l-2 border-slate-400 bg-slate-50 py-1.5 pl-3 pr-3 dark:border-slate-500 dark:bg-slate-800/60">
              <span className="font-mono text-[12px] font-bold uppercase tracking-[0.10em] text-slate-800 dark:text-slate-200">
                {execution.currentStageName.replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </section>

        {/* ── BLOCO 2: RISCO OPERACIONAL ── */}
        <section className={`border-b border-l-4 px-5 py-3 ${
          riskLevel === 'CRITICO' || !hasSla
            ? 'border-l-red-500 border-b-slate-200 dark:border-b-slate-800'
            : riskLevel === 'ALERTA'
              ? 'border-l-amber-500 border-b-slate-200 dark:border-b-slate-800'
              : 'border-l-green-500 border-b-slate-200 dark:border-b-slate-800'
        }`}>
          <div className="flex items-center gap-2">
            <span className="text-[12px] leading-none">{riskHeader.emoji}</span>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-300">{riskHeader.label}</p>
          </div>
          <div className="mt-2 space-y-1.5">
            {diagnostics.map((d, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="shrink-0 text-[12px] leading-[18px]">{d.icon}</span>
                <span className={`text-[12px] font-medium leading-[18px] ${
                  d.severity === 'critical' ? 'text-red-700 dark:text-red-300' :
                  d.severity === 'warning' ? 'text-amber-700 dark:text-amber-300' :
                  'text-slate-700 dark:text-slate-300'
                }`}>
                  {d.text}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── BLOCO 3: AÇÃO NECESSÁRIA ── */}
        <section className={`border-b border-l-4 px-5 py-3 ${
          nextActionMsg.severity === 'critical'
            ? 'border-l-red-500 border-b-slate-200 dark:border-b-slate-800'
            : nextActionMsg.severity === 'warning'
              ? 'border-l-amber-500 border-b-slate-200 dark:border-b-slate-800'
              : 'border-l-blue-500 border-b-slate-200 dark:border-b-slate-800'
        }`}>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Ação Necessária</p>
          <div className="mt-1.5 flex items-start gap-2">
            <ArrowRight size={14} className={`mt-0.5 shrink-0 ${
              nextActionMsg.severity === 'critical' ? 'text-red-600 dark:text-red-400' :
              nextActionMsg.severity === 'warning' ? 'text-amber-600 dark:text-amber-400' :
              'text-blue-600 dark:text-blue-400'
            }`} />
            <p className={`text-[13px] font-bold leading-[20px] ${
              nextActionMsg.severity === 'critical' ? 'text-red-800 dark:text-red-200' :
              nextActionMsg.severity === 'warning' ? 'text-amber-800 dark:text-amber-200' :
              'text-slate-800 dark:text-slate-200'
            }`}>
              {nextActionMsg.text}
            </p>
          </div>
        </section>

        {/* ── BLOCO 4: EXECUÇÃO ── */}
        <section className="border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Execução</p>
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCircle2 size={13} className="shrink-0 text-slate-400" />
                <span className="text-[11px] text-slate-500 dark:text-slate-400">Responsável</span>
              </div>
              <span className={`text-[12px] font-semibold ${
                executorLabel === 'Não atribuído'
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-slate-900 dark:text-slate-100'
              }`}>
                {executorLabel}
                {executorLabel === 'Não atribuído' && <span className="ml-1">⚠</span>}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield size={13} className="shrink-0 text-slate-400" />
                <span className="text-[11px] text-slate-500 dark:text-slate-400">Papel esperado</span>
              </div>
              <span className="text-[12px] font-semibold text-slate-900 dark:text-slate-100">{roleLabel}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock size={13} className="shrink-0 text-slate-400" />
                <span className="text-[11px] text-slate-500 dark:text-slate-400">Última movimentação</span>
              </div>
              <span className={`text-[12px] font-semibold ${
                lastAction ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'
              }`}>
                {lastAction ? formatRelativeTime(lastAction.timestamp) : 'Nenhuma'}
              </span>
            </div>
            {lastAction && (
              <div className="ml-5 border-l-2 border-slate-200 pl-2.5 dark:border-slate-700">
                <p className="text-[11px] font-medium text-slate-700 dark:text-slate-300">
                  {lastAction.actionDescription ?? lastAction.action}
                </p>
                {lastAction.performedByName && (
                  <p className="text-[10px] text-slate-400">por {lastAction.performedByName}</p>
                )}
              </div>
            )}
            {/* Tempo sem movimentação */}
            {idleLabel && !isTerminal && (
              <div className={`mt-1 flex items-center gap-2 rounded border-l-2 py-1.5 pl-2.5 ${
                idleSeverity === 'critical'
                  ? 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20'
                  : idleSeverity === 'warning'
                    ? 'border-l-amber-500 bg-amber-50/40 dark:bg-amber-950/15'
                    : 'border-l-slate-300 dark:border-l-slate-600'
              }`}>
                <span className="text-[12px]">⏱</span>
                <span className={`text-[12px] font-semibold ${
                  idleSeverity === 'critical' ? 'text-red-700 dark:text-red-300' :
                  idleSeverity === 'warning' ? 'text-amber-700 dark:text-amber-300' :
                  'text-slate-600 dark:text-slate-400'
                }`}>
                  Sem movimentação há {idleLabel}
                  {idleSeverity !== 'info' && ' ⚠'}
                </span>
              </div>
            )}
          </div>
        </section>

      </div>
    );
  };

  const showPause = Boolean(execution && execution.status === 'active' && canPause);
  const showResume = Boolean(execution && execution.status === 'on_hold' && canPause);
  const showCancel = Boolean(execution && execution.status !== 'completed' && execution.status !== 'cancelled' && canCancel);
  const isTerminal = Boolean(execution && (execution.status === 'completed' || execution.status === 'cancelled'));
  const isUnassigned = (currentTask?.assignedUserName ?? currentTask?.assignedToName) == null;

  return (
    <div className="flex h-full flex-col bg-[#f5f7fa] dark:bg-[var(--bg-workspace)]">
      <header className="flex items-center justify-between border-b border-slate-300/80 px-5 py-2.5 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex rounded border border-slate-300 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setDrawerTab('operacional')}
              className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${
                drawerTab === 'operacional'
                  ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                  : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
              }`}
            >
              Operacional
            </button>
            <button
              type="button"
              onClick={() => setDrawerTab('tecnico')}
              className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${
                drawerTab === 'tecnico'
                  ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                  : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
              }`}
            >
              Técnico
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="border border-slate-300 bg-white p-1.5 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
        >
          <X size={16} />
        </button>
      </header>

      {detailsQuery.error ? (
        <div className="mx-5 mt-4 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          <div className="flex items-center gap-2 font-semibold">
            <AlertCircle size={16} /> Falha ao carregar dados desta execução.
          </div>
          <p className="mt-1 text-xs">{detailsQuery.error.message}</p>
          <button type="button" className="mt-2 text-xs font-semibold underline" onClick={() => detailsQuery.refetch()}>
            Tentar novamente
          </button>
        </div>
      ) : null}

      {renderContent()}

      <footer className="border-t border-slate-300/80 px-5 py-2.5 dark:border-slate-800">
        <div className="grid grid-cols-5 gap-1.5">
          {/* Assumir */}
          <button
            type="button"
            disabled={actionBusy || !isUnassigned || isTerminal}
            className={`flex flex-col items-center gap-1 rounded border px-2 py-2 font-mono text-[10px] uppercase tracking-[0.10em] transition-colors disabled:opacity-40 ${
              isUnassigned && !isTerminal
                ? 'border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
            }`}
            title="Assumir responsabilidade pela tarefa atual"
          >
            <UserPlus size={16} />
            Assumir
          </button>

          {/* Pausar / Retomar */}
          {showResume ? (
            <button
              type="button"
              onClick={() => handleAction('resume')}
              disabled={actionBusy}
              className="flex flex-col items-center gap-1 rounded border border-slate-300 bg-white px-2 py-2 font-mono text-[10px] uppercase tracking-[0.10em] text-slate-700 transition-colors hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-blue-900/20"
            >
              <PlayCircle size={16} />
              Retomar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleAction('pause')}
              disabled={actionBusy || !showPause}
              className="flex flex-col items-center gap-1 rounded border border-slate-300 bg-white px-2 py-2 font-mono text-[10px] uppercase tracking-[0.10em] text-slate-700 transition-colors hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-amber-900/20"
            >
              <PauseCircle size={16} />
              Pausar
            </button>
          )}

          {/* Cancelar */}
          <button
            type="button"
            onClick={() => handleAction('cancel')}
            disabled={actionBusy || !showCancel}
            className="flex flex-col items-center gap-1 rounded border border-slate-300 bg-white px-2 py-2 font-mono text-[10px] uppercase tracking-[0.10em] text-slate-700 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-red-900/20"
          >
            <StopCircle size={16} />
            Cancelar
          </button>

          {/* Reprocessar */}
          <button
            type="button"
            onClick={() => setIsReprocessOpen((prev) => !prev)}
            disabled={!canTriggerReprocess}
            className="flex flex-col items-center gap-1 rounded border border-slate-300 bg-white px-2 py-2 font-mono text-[10px] uppercase tracking-[0.10em] text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          >
            <RefreshCcw size={16} />
            Reprocessar
          </button>

          {/* Finalizar */}
          <button
            type="button"
            disabled={isTerminal || !execution}
            className="flex flex-col items-center gap-1 rounded border border-green-300 bg-green-50 px-2 py-2 font-mono text-[10px] uppercase tracking-[0.10em] text-green-700 transition-colors hover:bg-green-100 disabled:opacity-40 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300"
            title="Finalizar execução"
          >
            <CheckCircle2 size={16} />
            Finalizar
          </button>
        </div>

        {isReprocessOpen && canTriggerReprocess ? (
          <div className="mt-3 space-y-3 border border-slate-300 bg-white p-4 text-sm dark:border-slate-800 dark:bg-[var(--bg-surface)]">
            <p className="font-semibold text-slate-900 dark:text-slate-100">Nova execução derivada</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Registre o motivo para manter auditoria e rastreabilidade.
            </p>
            <textarea
              value={reprocessReason}
              onChange={(event) => setReprocessReason(event.target.value)}
              rows={3}
              className="w-full border border-slate-300 bg-slate-50 p-2 text-sm focus:border-slate-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="border border-slate-300 bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                onClick={() => setIsReprocessOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleReprocess}
                disabled={mutations.reprocessExecution.isLoading}
                className="inline-flex items-center gap-2 border border-[#6f8094] bg-[#4c627b] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] text-white disabled:opacity-50"
              >
                {mutations.reprocessExecution.isLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                Confirmar reprocesso
              </button>
            </div>
          </div>
        ) : null}
      </footer>

      <ToastContainer />
    </div>
  );
}

function TimelineItem({ entry }: { entry: ExecutionTimelineEntry }) {
  return (
    <div className="border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-[var(--bg-surface)]">
      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>{new Date(entry.timestamp).toLocaleString('pt-BR')}</span>
        <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.12em]">
          <ArrowRight size={10} /> {entry.stageName ?? '---'}
        </span>
      </div>
      <p className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
        {entry.actionDescription ?? entry.action}
      </p>
      {entry.performedByName ? <p className="text-xs text-slate-500 dark:text-slate-400">Por {entry.performedByName}</p> : null}
      {entry.justification ? <p className="text-xs text-slate-500 dark:text-slate-400">{entry.justification}</p> : null}
    </div>
  );
}
